import { useCallback, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import useApiClient from 'hooks/use-api-client';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import type { CloudResourceApi } from '@env0/cloud-resource-service/api';
import type {
  CloudCompassAssetCountsQuery,
  CloudCompassAssetQuery,
  CloudCompassAssetsQuery,
  CloudCompassListParams,
  CloudCompassListResponse,
  CloudCompassListScope,
  FilterValuesParams
} from 'services/api-client/cloud-resources';

const STALE_TIME_MS = 5 * 60 * 1000;
const TEN_MINUTES_MS = 10 * 60 * 1000;
const ROW_LIST = { staleTime: STALE_TIME_MS, gcTime: TEN_MINUTES_MS };
const DRAWER = { staleTime: TEN_MINUTES_MS, gcTime: TEN_MINUTES_MS };
const EXPORT_POLL_MS = 1500;
export const EXPORT_TIMEOUT_MS = 5 * 60 * 1000;

type AssetPath = CloudResourceApi.CloudCompassAssetPath;

export const useCloudCompassQueryOptions = () => {
  const apiClient = useApiClient();
  const organizationId = useCurrentOrganizationId();

  return useMemo(
    () => ({
      filterValues: (dimension: string, params: FilterValuesParams = {}) => ({
        queryKey: [organizationId, 'cloud-compass', 'filter-values', dimension, params],
        queryFn: () => apiClient.cloudResources.getCloudCompassFilterValues(organizationId, dimension, params),
        staleTime: STALE_TIME_MS
      }),
      counts: (query: CloudCompassAssetCountsQuery) => ({
        queryKey: [organizationId, 'cloud-compass', 'counts', query],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssetCounts(organizationId, query),
        staleTime: STALE_TIME_MS
      }),
      tree: (query?: CloudCompassAssetQuery) => ({
        queryKey: [organizationId, 'cloud-compass', 'tree', ...(query ? [query] : [])],
        queryFn: () => apiClient.cloudResources.getCloudCompassTree(organizationId, query),
        staleTime: STALE_TIME_MS
      }),
      unmanagedAttribution: () => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-attribution'],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedAttribution(organizationId),
        staleTime: STALE_TIME_MS
      }),
      insights: (scope: CloudResourceApi.GetCloudCompassInsights.Scope) => ({
        queryKey: [organizationId, 'cloud-compass', 'insights', { scope }],
        queryFn: () => apiClient.cloudResources.getCloudCompassInsights(organizationId, scope),
        staleTime: STALE_TIME_MS
      }),
      unmanagedEnvironments: () => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-environments'],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedEnvironments(organizationId),
        staleTime: STALE_TIME_MS
      }),
      unmanagedRemainderTotal: () => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-remainder-total'],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedRemainderTotal(organizationId),
        staleTime: STALE_TIME_MS
      }),
      list: <Response = CloudCompassListResponse>(scope: CloudCompassListScope, params: CloudCompassListParams) => ({
        queryKey: [organizationId, 'cloud-compass', 'list', scope, params],
        queryFn: () => apiClient.cloudResources.getCloudCompassList<Response>(organizationId, scope, params),
        ...ROW_LIST
      }),
      listGroups: (
        scope: CloudCompassListScope,
        groupBy: string,
        params: Omit<CloudCompassListParams, 'sortBy' | 'sortDir'>
      ) => ({
        queryKey: [organizationId, 'cloud-compass', 'list-groups', scope, groupBy, params],
        queryFn: () => apiClient.cloudResources.getCloudCompassListGroups(organizationId, scope, groupBy, params),
        ...ROW_LIST
      }),
      listValues: (scope: CloudCompassListScope, dimension: string, q: string) => ({
        queryKey: [organizationId, 'cloud-compass', 'list-values', scope, dimension, q],
        queryFn: () => apiClient.cloudResources.getCloudCompassListValues(organizationId, scope, dimension, q),
        staleTime: STALE_TIME_MS
      }),
      listExport: (exportId: string) => ({
        queryKey: [organizationId, 'cloud-compass', 'list-export', exportId],
        queryFn: () => apiClient.cloudResources.getCloudCompassListExport(organizationId, exportId),
        refetchInterval: ({ state }: { state: { data?: { status: string }; dataUpdateCount: number } }) =>
          state.data?.status === 'running' && state.dataUpdateCount * EXPORT_POLL_MS < EXPORT_TIMEOUT_MS
            ? EXPORT_POLL_MS
            : false,
        staleTime: Infinity,
        gcTime: 0
      }),
      assetDetails: (path: AssetPath) => ({
        queryKey: [organizationId, 'cloud-compass', 'details', path],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssetDetails(organizationId, path),
        ...DRAWER
      }),
      metadataRows: (path: AssetPath, rows: AssetPath[]) => ({
        queryKey: [organizationId, 'cloud-compass', 'metadata-rows', path, rows],
        queryFn: () => apiClient.cloudResources.readCloudCompassMetadataRows(organizationId, path, rows),
        ...DRAWER
      }),
      related: (path: AssetPath) => ({
        queryKey: [organizationId, 'cloud-compass', 'related', path],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssetRelated(organizationId, path),
        ...DRAWER
      }),
      // Re-checked on every dialog open, so a cached answer would defeat it.
      managedStatus: (resources: AssetPath[]) => ({
        queryKey: [organizationId, 'cloud-compass', 'managed-status', resources],
        queryFn: () => apiClient.cloudResources.checkCloudCompassManagedStatus(organizationId, resources),
        staleTime: 0,
        gcTime: 0
      }),
      assets: (query: CloudCompassAssetsQuery) => ({
        queryKey: [organizationId, 'cloud-compass', 'assets', query],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssets(organizationId, query),
        ...ROW_LIST
      })
    }),
    [apiClient, organizationId]
  );
};

export const useFetchCloudCompassFilterValues = () => {
  const queryClient = useQueryClient();
  const { filterValues } = useCloudCompassQueryOptions();

  return useCallback(
    (dimension: string, params?: FilterValuesParams) => queryClient.fetchQuery(filterValues(dimension, params)),
    [queryClient, filterValues]
  );
};

export const useCloudCompassFilterValues = (dimensions: string[]) => {
  const { filterValues } = useCloudCompassQueryOptions();

  return useQueries({
    queries: dimensions.map(dimension => filterValues(dimension)),
    combine: results =>
      Object.fromEntries(dimensions.map((dimension, index) => [dimension, results[index].data?.values ?? []]))
  });
};
